import { useEffect, useState } from 'react';
import { Modal, Descriptions, Input, Button, Tag, Space, message, Spin, Typography } from 'antd';
import { Save, Pencil, X, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, fmt } from '../api';
import { CATEGORY_KO } from '../labels';

const { Text } = Typography;

type MerchantDetail = {
  mid: string;
  name: string;
  start_date: string | null;
  term_days: number;
  deposit_amount: number;
  purchase_amount: number;
  fee_rate: number;
  category: 'repay' | 'normal' | 'bad';
  effective_category: 'repay' | 'normal' | 'bad';
  auto_bad: boolean;
  active: number;
  payout_method: 'bank' | 'wallet' | null;
  people_id: string | null;
  payout_bank: string | null;
  payout_account: string | null;
  payout_holder: string | null;
  repay_bank: string | null;
  repay_account: string | null;
  repay_holder: string | null;
  admin_memo: string | null;
  admin_memo_updated_at: string | null;
  admin_memo_updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  remaining_days: number | null;
  remaining_amount: number | null;
  cumulative_repaid: number | null;
  latest_settle_date: string | null;
  prev_shortfall: number | null;
  principal_repaid_today: number | null;
  principal_repaid_base?: number | null;
  return_adj_today_sum?: number | null;
  remaining_adj_sum?: number | null;
  return_adj_sum?: number | null;
  last_repay_date: string | null;
  last_repay_amount: number | null;
  last_sales_date: string | null;
  last_sales_amount: number | null;
  ceo_name: string | null;
  contact_phone: string | null;
  business_no: string | null;
  business_address: string | null;
};

export default function MerchantDetailModal({
  mid, open, onClose,
}: {
  mid: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [data, setData] = useState<MerchantDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [memo, setMemo] = useState('');
  const [saving, setSaving] = useState(false);

  // 카테고리 변경 이력
  type HistRow = { id: number; from_category: string | null; to_category: string; changed_at: string; changed_by: string | null; reason: string | null };
  const [history, setHistory] = useState<HistRow[]>([]);
  const [editingReasonId, setEditingReasonId] = useState<number | null>(null);
  const [reasonDraft, setReasonDraft] = useState('');
  // 필드별 변경 이력 (등록/수정 diff)
  type ChangeRow = { id: number; actor: string; action: 'create'|'update'; field: string; from_value: string | null; to_value: string | null; changed_at: string };
  const [changeHistory, setChangeHistory] = useState<ChangeRow[]>([]);
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const FIELD_KO: Record<string, string> = {
    name: '가맹점명', start_date: '시작일', term_days: '기간', deposit_amount: '입금액',
    purchase_amount: '매입액', fee_rate: '수수료율', category: '고객분류', active: '활성상태',
    payout_method: '정산방식', people_id: '피플 ID',
    payout_bank: '정산 은행', payout_account: '정산 계좌번호', payout_holder: '정산 예금주', payout_memo: '정산 통장 표시',
    repay_bank: '반환 은행', repay_account: '반환 계좌번호', repay_holder: '반환 예금주', repay_memo: '반환 통장 표시',
    ceo_name: '대표자명', contact_phone: '연락처', business_no: '사업자등록번호', business_address: '사업장 주소',
  };
  const fmtFieldValue = (field: string, v: string | null): string => {
    if (v == null) return '(비어 있음)';
    if (field === 'fee_rate') return (Number(v) * 100).toFixed(2) + '%';
    if (field === 'category') return CATEGORY_KO[v] ?? v;
    if (field === 'active')   return v === '1' ? '활성' : '비활성';
    if (field === 'payout_method') return v === 'wallet' ? '지갑정산' : '은행정산';
    if (['deposit_amount', 'purchase_amount'].includes(field)) return Number(v).toLocaleString('ko-KR') + '원';
    return v;
  };

  useEffect(() => {
    if (!open || !mid) { setData(null); return; }
    let alive = true;
    setLoading(true); setErr(null); setEditing(false);
    (async () => {
      try {
        const r = await api<{ data: MerchantDetail }>('GET', `/merchants/${encodeURIComponent(mid)}`);
        if (!alive) return;
        setData(r.data);
        setMemo(r.data.admin_memo ?? '');
        // 카테고리 이력 병행 조회 (실패해도 본문 영향 없게 try)
        try {
          const h = await api<{ data: HistRow[] }>('GET', `/merchants/${encodeURIComponent(mid)}/category-history`);
          if (alive) setHistory(h.data ?? []);
        } catch { if (alive) setHistory([]); }
        // 필드별 변경 이력 병행 조회
        try {
          const ch = await api<{ data: ChangeRow[] }>('GET', `/merchants/${encodeURIComponent(mid)}/change-history?limit=200`);
          if (alive) setChangeHistory(ch.data ?? []);
        } catch { if (alive) setChangeHistory([]); }
      } catch (e: any) {
        if (alive) setErr(e?.message ?? '조회 실패');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open, mid]);

  const saveMemo = async () => {
    if (!mid) return;
    setSaving(true);
    try {
      await api('PATCH', `/merchants/${encodeURIComponent(mid)}/memo`, { memo });
      message.success('메모 저장됨');
      // 갱신 시각 즉시 반영
      setData(d => d ? {
        ...d,
        admin_memo: memo,
        admin_memo_updated_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
      } : d);
      setEditing(false);
    } catch (e: any) {
      message.error(e?.message ?? '저장 실패');
    } finally { setSaving(false); }
  };

  return (
    <Modal
      title={
        <Space size={10}>
          <span>가맹점 상세</span>
          {data && <Tag color={data.active ? 'green' : 'default'} bordered={false}>{data.active ? '정식' : '미등록'}</Tag>}
          {data && (() => {
            const eff = data.effective_category ?? data.category;
            return (
              <Tag bordered={false} className={`chip cat-${eff}`} style={{ margin: 0 }}
                   title={data.auto_bad ? '약정 기간 종료 후 잔여액이 남아 자동 악성 분류됨' : undefined}>
                {CATEGORY_KO[eff] ?? eff}
                {data.auto_bad && <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, opacity: 0.85 }}>자동</span>}
              </Tag>
            );
          })()}
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnClose
    >
      {loading ? (
        <div style={{ padding: '40px 0', textAlign: 'center' }}><Spin /></div>
      ) : err ? (
        <Text type="danger">{err}</Text>
      ) : !data ? null : (
        <>
          <Descriptions
            size="small"
            column={2}
            bordered
            labelStyle={{ width: 110, color: 'var(--text-muted)', fontWeight: 600, fontSize: 12 }}
            contentStyle={{ fontSize: 13 }}
            items={[
              { key: 'name',  label: '가맹점명',
                children: <span style={{ fontWeight: 700 }}>{data.name}</span> },
              { key: 'mid',   label: 'MID',
                children: <span style={{ fontFamily: 'monospace' }}>{data.mid}</span> },
              { key: 'start', label: '시작일', children: fmt.date(data.start_date) },
              { key: 'term',  label: '기간',   children: `${data.term_days}일` },
              { key: 'rdays', label: '잔여기간',
                children: data.remaining_days == null
                  ? <span style={{ color: 'var(--text-muted)' }}>-</span>
                  : <span style={data.remaining_days === 0 ? { color: '#7f1d1d', fontWeight: 700 } : undefined}>{data.remaining_days}일</span>
              },
              { key: 'dep',   label: '입금액', children: fmt.won(data.deposit_amount) },
              { key: 'pur',   label: '매입액', children: fmt.won(data.purchase_amount) },
              { key: 'prev_short', label: '전일미차감',
                children: data.prev_shortfall == null
                  ? <span style={{ color: 'var(--text-muted)' }}>-</span>
                  : (
                    <span>
                      <span style={{ fontWeight: 600, color: (data.prev_shortfall ?? 0) > 0 ? '#c2410c' : undefined }}>
                        {fmt.won(data.prev_shortfall)}원
                      </span>
                      {((data as any).shortfall_adj_sum ?? 0) !== 0 && (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}
                              title={`조정 누적 ${((data as any).shortfall_adj_sum ?? 0) >= 0 ? '+' : ''}${fmt.won((data as any).shortfall_adj_sum ?? 0)}원 반영됨`}>
                          (조정 반영)
                        </span>
                      )}
                    </span>
                  )
              },
              // 문서3 §3: "최근 원금 반환 50,000원 / 최근 반환일 2026-08-18" 형식.
              // 이력 없으면 "없음 / -". last_repay_amount / last_repay_date 우선 (실제 반환 발생일).
              { key: 'p_repaid', label: '최근 원금 반환',
                children: data.last_repay_date == null
                  ? <span style={{ color: 'var(--text-muted)' }}>없음</span>
                  : (
                    <span style={{ fontWeight: 600, color: 'var(--leaf-700)' }}>
                      {fmt.won(data.last_repay_amount ?? 0)}원
                    </span>
                  )
              },
              { key: 'last_repay', label: '최근 반환일',
                children: data.last_repay_date
                  ? (
                    <span>
                      <span style={{ fontFamily: 'monospace' }}>{data.last_repay_date}</span>
                      {(data.principal_repaid_today ?? 0) > 0 && data.principal_repaid_today !== data.last_repay_amount && (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 8 }}>
                          (오늘 반환 {fmt.won(data.principal_repaid_today)}원
                          {(data.return_adj_today_sum ?? 0) !== 0 && ' · 조정 반영'}
                          )
                        </span>
                      )}
                    </span>
                  )
                  : <span style={{ color: 'var(--text-muted)' }}>-</span>
              },
              { key: 'ramt',  label: '당일 잔여액',
                children: data.remaining_amount == null
                  ? <span style={{ color: 'var(--text-muted)' }}>-</span>
                  : (
                    <span>
                      <span style={data.remaining_amount === 0
                        ? { color: 'var(--leaf-700)', fontWeight: 700 }
                        : { fontWeight: 600 }}>
                        {fmt.won(data.remaining_amount)}원
                      </span>
                      {data.latest_settle_date && (
                        <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>
                          (정산 {data.latest_settle_date} 기준 · 누적회수 {fmt.won(data.cumulative_repaid)}원)
                        </span>
                      )}
                    </span>
                  )
              },
              { key: 'fee',   label: '수수료율', children: fmt.pct(data.fee_rate) },
              { key: 'lsd',   label: '마지막 매출일',
                children: data.last_sales_date
                  ? <span style={{ fontFamily: 'monospace' }}>{data.last_sales_date}</span>
                  : <span style={{ color: 'var(--text-muted)' }}>매출 없음</span>
              },
              { key: 'lsa',   label: '마지막 매출금',
                children: data.last_sales_date
                  ? <span style={{ fontWeight: 600 }}>{fmt.won(data.last_sales_amount ?? 0)}</span>
                  : <span style={{ color: 'var(--text-muted)' }}>-</span>
              },
              { key: 'cat',   label: '고객분류',
                children: (() => {
                  const eff = data.effective_category ?? data.category;
                  if (!data.auto_bad) return CATEGORY_KO[data.category] ?? data.category;
                  return (
                    <span>
                      <b style={{ color: 'var(--red-700)' }}>{CATEGORY_KO[eff] ?? eff}</b>
                      <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 6 }}>
                        (등록값 {CATEGORY_KO[data.category]} · 약정 만기 후 잔여액으로 자동 분류)
                      </span>
                    </span>
                  );
                })()
              },
              { key: 'ceo',   label: '대표자명', children: data.ceo_name || '-' },
              { key: 'phone', label: '연락처',   children: data.contact_phone || '-' },
              { key: 'bno',   label: '사업장 번호', children: data.business_no || '-' },
              { key: 'addr',  label: '사업장 주소지', span: 2, children: data.business_address || '-' },
              { key: 'pm',    label: '정산방식',
                children: (data.payout_method ?? 'bank') === 'wallet' ? '지갑정산' : '은행정산' },
              { key: 'pinfo', label: '정산 수단',
                children: (data.payout_method ?? 'bank') === 'wallet'
                  ? (data.people_id ? <span style={{ fontFamily: 'monospace' }}>피플 ID {data.people_id}</span> : <span style={{ color: 'var(--warn)' }}>피플 ID 미입력</span>)
                  : (data.payout_bank ? `${data.payout_bank} ${data.payout_account ?? ''} (${data.payout_holder ?? '-'})` : '-')
              },
              { key: 'rinfo', label: '반환 계좌', span: 2,
                children: data.repay_bank
                  ? `${data.repay_bank} ${data.repay_account ?? ''} (${data.repay_holder ?? '-'})`
                  : '-'
              },
              { key: 'ts',    label: '등록/수정', span: 2,
                children: (
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    등록 {data.created_at ?? '-'} · 수정 {data.updated_at ?? '-'}
                  </span>
                )
              },
            ]}
          />

          {/* 관리자 메모 */}
          <div style={{ marginTop: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
              <div>
                <Text strong style={{ fontSize: 13 }}>관리자 메모</Text>
                {data.admin_memo_updated_at && (
                  <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                    최종 {data.admin_memo_updated_at}
                    {data.admin_memo_updated_by ? ` · ${data.admin_memo_updated_by}` : ''}
                  </Text>
                )}
              </div>
              {!editing && (
                <Button size="small" icon={<Pencil size={12} />} onClick={() => setEditing(true)}>
                  {data.admin_memo ? '수정' : '메모 추가'}
                </Button>
              )}
            </div>

            {editing ? (
              <>
                <Input.TextArea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  autoSize={{ minRows: 4, maxRows: 12 }}
                  placeholder="이 가맹점에 대한 운영 메모를 자유롭게 기록 (연락처·특이사항·계약 비고 등)"
                  maxLength={4000}
                  showCount
                />
                <Space style={{ marginTop: 10, justifyContent: 'flex-end', width: '100%' }}>
                  <Button icon={<X size={13} />} onClick={() => { setEditing(false); setMemo(data.admin_memo ?? ''); }}>취소</Button>
                  <Button type="primary" icon={<Save size={13} />} onClick={saveMemo} loading={saving}>저장</Button>
                </Space>
              </>
            ) : (
              <div style={{
                padding: '12px 14px',
                background: 'var(--surface-alt, #fafbfa)',
                border: '1px solid var(--border, #e6ece9)',
                borderRadius: 8,
                minHeight: 64,
                whiteSpace: 'pre-wrap',
                fontSize: 13,
                lineHeight: 1.65,
                color: data.admin_memo ? 'var(--text)' : 'var(--text-muted)',
              }}>
                {data.admin_memo || '메모 없음 — "메모 추가"를 눌러 작성하세요.'}
              </div>
            )}
          </div>

          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Link to={`/merchants/${encodeURIComponent(data.mid)}`} onClick={onClose}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
              가맹점 수정 화면으로 <ExternalLink size={12} />
            </Link>
          </div>

          {/* 필드별 등록/수정 변경 이력 — v21 감사 이력 */}
          <div style={{ marginTop: 18, borderTop: '1px solid var(--border-soft)', paddingTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text strong style={{ fontSize: 13 }}>
                등록·수정 이력 <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>({changeHistory.length}건)</Text>
              </Text>
              <Button size="small" type="link" onClick={() => setShowChangeHistory(v => !v)} style={{ padding: 0 }}>
                {showChangeHistory ? '숨기기' : (changeHistory.length > 0 ? '전체 보기' : '')}
              </Button>
            </div>
            {changeHistory.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>변경 이력 없음</div>
            ) : showChangeHistory ? (
              <div className="table-wrap" style={{ maxHeight: 280, border: '1px solid var(--border-soft)', borderRadius: 6 }}>
                <table>
                  <thead><tr>
                    <th className="nowrap">시각 (KST)</th>
                    <th className="nowrap">처리자</th>
                    <th className="nowrap">유형</th>
                    <th className="nowrap">항목</th>
                    <th>변경 전</th>
                    <th>변경 후</th>
                  </tr></thead>
                  <tbody>
                    {changeHistory.map(h => (
                      <tr key={h.id}>
                        <td className="nowrap" style={{ fontFamily: 'monospace', fontSize: 11 }}>{h.changed_at}</td>
                        <td className="nowrap" style={{ fontFamily: 'monospace', fontSize: 11 }}>{h.actor}</td>
                        <td className="nowrap">
                          <Tag color={h.action === 'create' ? 'green' : 'blue'} style={{ margin: 0 }}>
                            {h.action === 'create' ? '등록' : '수정'}
                          </Tag>
                        </td>
                        <td className="nowrap" style={{ fontSize: 12 }}>{FIELD_KO[h.field] ?? h.field}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{fmtFieldValue(h.field, h.from_value)}</td>
                        <td style={{ fontSize: 12, fontWeight: 600 }}>{fmtFieldValue(h.field, h.to_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                최근 {Math.min(3, changeHistory.length)}건 요약:
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                  {changeHistory.slice(0, 3).map(h => (
                    <li key={h.id}>
                      <span style={{ fontFamily: 'monospace' }}>{h.changed_at}</span>{' '}
                      · <b>{FIELD_KO[h.field] ?? h.field}</b>{' '}
                      {fmtFieldValue(h.field, h.from_value)} → <b>{fmtFieldValue(h.field, h.to_value)}</b>{' '}
                      <span style={{ opacity: 0.7 }}>by {h.actor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* 카테고리 변경 이력 — 분류가 바뀌어도 과거 분류 기록이 남는다. */}
          <div style={{ marginTop: 18, borderTop: '1px solid var(--border-soft)', paddingTop: 12 }}>
            <Text strong style={{ fontSize: 13 }}>분류 변경 이력</Text>
            {history.length === 0 ? (
              <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: 12 }}>변경 이력 없음</div>
            ) : (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {history.map(h => (
                  <div key={h.id} style={{
                    display: 'flex', flexDirection: 'column', gap: 4,
                    padding: '8px 10px', background: 'var(--bg-soft, #f8fafc)',
                    borderRadius: 6, fontSize: 12,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{h.changed_at}</span>
                      <Tag bordered={false} className={`chip cat-${h.from_category ?? 'normal'}`} style={{ margin: 0 }}>
                        {h.from_category ? (CATEGORY_KO[h.from_category] ?? h.from_category) : '최초 등록'}
                      </Tag>
                      <span>→</span>
                      <Tag bordered={false} className={`chip cat-${h.to_category}`} style={{ margin: 0 }}>
                        {CATEGORY_KO[h.to_category] ?? h.to_category}
                      </Tag>
                      {h.changed_by && <span style={{ color: 'var(--text-muted)' }}>by {h.changed_by}</span>}
                    </div>
                    {editingReasonId === h.id ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Input
                          size="small"
                          value={reasonDraft}
                          onChange={e => setReasonDraft(e.target.value)}
                          placeholder="변경 사유 (예: 계약 종료에 따른 분류 변경)"
                          autoFocus
                        />
                        <Button size="small" type="primary" onClick={async () => {
                          try {
                            await api('PATCH', `/merchants/${encodeURIComponent(mid!)}/category-history/${h.id}`, { reason: reasonDraft });
                            setHistory(prev => prev.map(x => x.id === h.id ? { ...x, reason: reasonDraft } : x));
                            setEditingReasonId(null);
                          } catch (e: any) { message.error(e?.message ?? '저장 실패'); }
                        }}>저장</Button>
                        <Button size="small" onClick={() => setEditingReasonId(null)}>취소</Button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: h.reason ? 'var(--text)' : 'var(--text-muted)' }}>
                          {h.reason || '사유 미입력'}
                        </span>
                        <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
                                onClick={() => { setEditingReasonId(h.id); setReasonDraft(h.reason ?? ''); }}>
                          {h.reason ? '수정' : '사유 추가'}
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
